import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRoute, useLocation } from 'wouter';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { CmdLoader, CmdSimpleSelectOption } from '@commander-services/gui-components';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import Form from './Form';
import PermissionsService, { PERMISSION_PERIOD_LOCK } from '../../Services/PermissionsService';
import { IDynamicTableAddButton } from '../../interfaces/tables';
import ApiService from '../../Services/ApiService';
import { TABLE_PERIOD_LOCK, TABLE_PERIOD_LOCK_DEFINITION } from '../../Services/TableService';
import { tabsByPermission } from '.';
import TabComponent from '../TabComponent';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { CmdTable } from '../CmdTable/CmdTable';
import { ICmdTableColumn, ICmdTableFilter } from '../CmdTable/types';
import { CUSTOMERS_KEY, REQUIRED } from '../CmdTable/constants';
import {
  PERIOD_LOCK_ACTIONS_KEY,
  PERIOD_LOCK_CUSTOMER_ID_KEY,
  PERIOD_LOCK_DEFINITION_CUSTOMER_ID_KEY,
  PERIOD_LOCK_DEFINITION_CUSTOMERS,
  PERIOD_LOCK_DEFINITION_ENABLED,
  PERIOD_LOCK_DEFINITION_SETTINGS_SAVE,
  PERIOD_LOCK_DEFINITION_TABLE_KEY,
  PERIOD_LOCK_DEFINITION_URL,
  PeriodLockDefinitionTranslationKeys,
} from './constants';
import CmdTableSettings from '../CmdTable/settings/CmdTableSettings';
import FilterChips from '../CmdTable/FilterChips/FilterChips';
import usePeriodLogDefinition from './usePeriodLockDefinition';
import { PeriodLockDefinitionRecord, PeriodLockDefinitionResponse } from './interfaces';
import { customersAtom, selectedCustomersAtom } from '../../store/recoil/customers';
import { arrayOrder, getTableSettings } from '../CmdTable/CmdTableService';
import {
  columnsAtom,
  filterForRequestAtomFamily,
  sortFilterAtomFamily,
  tableFiltersAtomFamily,
} from '../CmdTable/CmdTableState';
import PageHeader from '../PageHeader';

export default function PeriodLockSetting() {
  const { formatMessage: f } = useIntl();
  const [isSubpageOpened, setIsSubpageOpened] = React.useState<boolean>(false);
  const [customerOptions, setCustomerOptions] = React.useState<CmdSimpleSelectOption[] | null>(
    null
  );
  const [callTable, setCallTable] = React.useState<boolean>(false);
  const [itemId, setItemId] = React.useState<number | null>(null);
  const [isEdit, setIsEdit] = React.useState<boolean>(false);
  const [loadedCustomerIds, setLoadedCustomerIds] = React.useState<string[]>([]);
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const [pathname, navigate] = useLocation();
  const [, params] = useRoute(`${PERIOD_LOCK_DEFINITION_URL}/edit/:id`);
  const { setDocumentTitle } = useDocumentTitle();
  const tableFiltersKey = useRecoilValue(tableFiltersAtomFamily(PERIOD_LOCK_DEFINITION_TABLE_KEY));
  const setColumnsTable = useSetRecoilState<ICmdTableColumn[]>(columnsAtom);
  const customer = useRecoilValue(customersAtom);
  const queryClient = useQueryClient();
  const filterForRequest = useRecoilValue<ICmdTableFilter>(
    filterForRequestAtomFamily(PERIOD_LOCK_DEFINITION_TABLE_KEY)
  );
  const sortFilter = useRecoilValue<{ [key: string]: string }>(
    sortFilterAtomFamily(PERIOD_LOCK_DEFINITION_TABLE_KEY)
  );
  const { columns, columnOrder, columnVisibility } = usePeriodLogDefinition();

  const tabs = React.useState(
    tabsByPermission(
      [TABLE_PERIOD_LOCK, TABLE_PERIOD_LOCK_DEFINITION],
      TABLE_PERIOD_LOCK_DEFINITION
    )
  )[0];

  const handleClose = React.useCallback(() => {
    if (pathname.includes('add') || pathname.includes('edit'))
      navigate(`${PERIOD_LOCK_DEFINITION_URL}`);
    setIsSubpageOpened(false);
    setItemId(null);
  }, [navigate, pathname]);

  const updateTable = () => {
    queryClient.invalidateQueries({
      queryKey: [PERIOD_LOCK_DEFINITION_TABLE_KEY, filterForRequest, sortFilter, selectedCustomers],
    });
    queryClient.invalidateQueries({
      queryKey: [PERIOD_LOCK_DEFINITION_CUSTOMERS, selectedCustomers],
    });
  };

  const getCustomers = async () => {
    const url = `/v1/period-lock-definition/form/customer-list`;
    const response = await ApiService.get(url);
    if (response.data && response.data.customerList && response.status === 200) {
      const options = response.data.customerList;
      const filteredOptions = options.filter((i) => selectedCustomers.includes(i.value));

      setCustomerOptions(filteredOptions);
    }
  };

  const buttons: IDynamicTableAddButton = {};
  buttons[TABLE_PERIOD_LOCK_DEFINITION] = {
    open: ['addSetting'],
  };

  React.useEffect(() => {
    if (pathname === `${PERIOD_LOCK_DEFINITION_URL}`) {
      setIsSubpageOpened(false);
      const documentTitle = `${f({ id: 'tab.period-lock-definition' })} - ${f({
        id: 'menu.periodLock',
      })} - ${f({
        id: 'menu.settings',
      })}`;
      setDocumentTitle(documentTitle);
    } else if (pathname === `${PERIOD_LOCK_DEFINITION_URL}/add`) {
      setIsSubpageOpened(true);
      setIsEdit(false);
      setCallTable(false);
      const documentTitle = `${f({ id: 'periodLock.addSetting' })} - ${f({
        id: 'tab.period-lock-definition',
      })} - ${f({
        id: 'menu.periodLock',
      })} - ${f({
        id: 'menu.settings',
      })}`;
      setDocumentTitle(documentTitle);
    } else if (params) {
      setItemId(Number(params.id));
      setIsSubpageOpened(true);
      const documentTitle = `${f({ id: 'periodLock.editSetting' })} - ${f({
        id: 'tab.period-lock-definition',
      })} - ${f({
        id: 'menu.periodLock',
      })} - ${f({
        id: 'menu.settings',
      })}`;
      setDocumentTitle(documentTitle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const isCreatingAllowed =
    PermissionsService.hasAccess(PERMISSION_PERIOD_LOCK, ['create'], selectedCustomers) &&
    customerOptions &&
    customerOptions.length > 0;

  const handleAddButton = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    navigate(`${PERIOD_LOCK_DEFINITION_URL}/add`);
  };

  const handleSetOrderColumns = (
    newOrderColumns: { name: string; hidden: boolean; visibility: string; sort: boolean }[]
  ) => {
    const tempOrder = newOrderColumns.map((item) => item.name);

    const tempTableSettingOrder: ICmdTableColumn[] = [];

    newOrderColumns.map((item) => {
      if (customer.length === 1 && item.name === CUSTOMERS_KEY) {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: true,
        });
      } else {
        tempTableSettingOrder.push({
          key: item.name,
          default: item.visibility === REQUIRED,
          hidden: item.hidden,
        });
      }
    });

    tempTableSettingOrder.push({ key: 'actions', hidden: false, default: false });

    const orderColumns = arrayOrder([...tempTableSettingOrder], [...tempOrder], 'key');

    setColumnsTable(orderColumns);
  };

  const getSettings = async () => {
    const settings = await getTableSettings(PERIOD_LOCK_DEFINITION_TABLE_KEY);
    if (settings) {
      handleSetOrderColumns(settings.columns);
      return settings;
    }
    return false;
  };

  const results = useQueries({
    queries: [
      {
        queryKey: [`cmdTableSettings-${PERIOD_LOCK_DEFINITION_TABLE_KEY}`],
        queryFn: () => getSettings(),
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
      {
        queryKey: [PERIOD_LOCK_DEFINITION_CUSTOMERS, selectedCustomers],
        queryFn: () => getCustomers(),
        staleTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const { isLoading: isLoadingSettings } = results[0];
  const { isLoading: isLoadingCustomers } = results[1];

  const isLoading = isLoadingSettings || isLoadingCustomers;

  return (
    <div>
      <PageHeader
        title={`${f({ id: 'menu.settings' })} / ${f({ id: 'menu.periodLock' })}`}
        onlineHelpTopic="period-lock"
        show={!isSubpageOpened}
      />
      <div className="w-page mt-8">
        <div className="w-page-content">
          <div>
            {isSubpageOpened ? (
              <Form
                isEdited={isEdit}
                closeCallback={handleClose}
                editingItemId={itemId}
                updateTable={updateTable}
                customerOptions={
                  customerOptions
                    ? customerOptions.filter((i) => !loadedCustomerIds.includes(String(i.value)))
                    : []
                }
              />
            ) : (
              <div>
                <div className="container-fluid">
                  <TabComponent headerStyles={{ marginTop: 0 }} tabs={tabs} />
                  <section className="basic-box border-top-thin border-top-gray">
                    <div className="basic-box__content">
                      <div className="row no-gutters mb-2">
                        <div className="col-12 col-md-auto mr-md-2 mb-2">
                          {isCreatingAllowed && (
                            <a
                              href="#"
                              data-cy="table-record-addBtn"
                              title={f({ id: 'periodLock.addSetting' })}
                              className="e-button e-button--gray"
                              data-id="addAlarm"
                              onClick={handleAddButton}
                            >
                              {f({ id: 'periodLock.addSetting' })}
                            </a>
                          )}
                        </div>
                        <div className="col col-md" />
                        <div className="col-auto col-md-12 mt-2 ml-2 mt-md-0 ml-md-0 col-lg-auto order-5 order-lg-4 mt-lg-2 mr-2" />
                        <div className="col-auto order-4 order-lg-5">
                          <CmdTableSettings
                            tableId={PERIOD_LOCK_DEFINITION_TABLE_KEY}
                            tableName={PERIOD_LOCK_DEFINITION_SETTINGS_SAVE}
                            sort={{ column: PERIOD_LOCK_CUSTOMER_ID_KEY, direction: 'desc' }}
                            translationKeys={PeriodLockDefinitionTranslationKeys}
                          />
                        </div>
                      </div>
                      <div className="d-flex flex-wrap mb-2">
                        {tableFiltersKey &&
                          tableFiltersKey.map((filterId) => (
                            <FilterChips
                              key={filterId}
                              id={filterId}
                              tableName={PERIOD_LOCK_DEFINITION_TABLE_KEY}
                            />
                          ))}
                      </div>
                      {isLoading && <CmdLoader inContent={true} />}
                      <CmdTable<PeriodLockDefinitionResponse, PeriodLockDefinitionRecord>
                        name={PERIOD_LOCK_DEFINITION_TABLE_KEY}
                        stickyColumns={[PERIOD_LOCK_ACTIONS_KEY]}
                        lockedColumns={[
                          PERIOD_LOCK_DEFINITION_CUSTOMER_ID_KEY,
                          PERIOD_LOCK_DEFINITION_ENABLED,
                        ]}
                        columns={columns}
                        columnOrder={columnOrder}
                        columnVisibility={columnVisibility}
                      />
                    </div>
                  </section>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
